import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Flex,
  Heading,
  HStack,
  Icon,
  Link,
  Stack,
  Tag,
  Text,
} from "@chakra-ui/react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { FiChevronRight, FiExternalLink, FiFileText } from "react-icons/fi";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { PublicationChannel, SourcingEventStatus, SourcingEventV2Dto } from "../../../autogen/bff-api";
import { useDisplayDateWithTime } from "../../../common/formatting/useDisplayDateWithTime";
import { Explanation } from "../../../common/support/Explanation/Explanation";
import { urls } from "../../../urls";
import {
  formatEstimatedValue,
  getContractNatureLabel,
  getProcedureLabel,
} from "../../sourcing-events/portal/utils/displayFormatters";
import { isDpsScheme, isQualificationScheme } from "../../sourcing-events/buyer/wizard/utils/procedure";
import { LabelValueGrid } from "../../sourcing-events/portal/view-single/LabelValueGrid";
import { DevFastForwardMenu } from "./DevFastForwardMenu";
import { EventLifecycleMenu } from "./EventLifecycleMenu";
import { SourcingEventV2StatusTag } from "./SourcingEventV2StatusTag";
import {
  competitionStage,
  effectiveBidDeadline,
  effectiveRequestDeadline,
  type CompetitionStage,
} from "./qualification/stage";
import { useProcurementTerms } from "../../../common/terminology/useProcurementTerms";
import { buildDoffinNoticeUrl } from "./doffinNoticeUrl";
import { RoundPager } from "./rounds/RoundPager";
import { useRoundSelection } from "./rounds/RoundSelectionContext";

const DASH = "–";

const personName = (p: { firstName?: string | null; lastName?: string | null; email: string }) =>
  [p.firstName, p.lastName].filter(Boolean).join(" ") || p.email;

// t('Request phase') t('Qualification phase') t('Tender phase')
const STAGE_LABEL_KEYS: Record<CompetitionStage, string> = {
  REQUESTS_OPEN: "Request phase",
  QUALIFICATION: "Qualification phase",
  TENDERING: "Tender phase",
};

const PROTOCOL_STATUSES: SourcingEventStatus[] = [
  SourcingEventStatus.Evaluation,
  SourcingEventStatus.Completed,
  SourcingEventStatus.AwardDecision,
  SourcingEventStatus.Awarded,
  SourcingEventStatus.Closed,
];

interface Props {
  event: SourcingEventV2Dto;
  onViewPublic?: () => void;
  responsibleEditControl?: ReactNode;
  shareControl?: ReactNode;
}

export const EventHeader = ({ event, onViewPublic, responsibleEditControl, shareControl }: Props) => {
  const { t } = useTranslation();
  const terms = useProcurementTerms();
  const navigate = useNavigate();
  const displayDateWithTime = useDisplayDateWithTime();
  const canGenerateProtocol = PROTOCOL_STATUSES.includes(event.status);
  const { responsiblePerson } = event;
  const { eventData } = event;
  const publicContext = event.eventContext.type === "Public" ? event.eventContext : null;
  const isPrivateEvent = event.eventContext.type === "Private";
  const value = event.eventContext.estimatedValue;
  const contractNature = event.eventContext.contractNature;
  const doffinReference = publicContext?.publications?.find(
    (p) => p.channel === PublicationChannel.Doffin && p.externalId,
  )?.externalId;
  const stage = competitionStage(event);
  const { selectedRound } = useRoundSelection();
  // With a round selected, the header speaks for that round — its deadline is the frist.
  const bidDeadline = selectedRound?.deadline ?? effectiveBidDeadline(event);
  const requestDeadline = effectiveRequestDeadline(event);
  const isTwoStage = stage != null;

  return (
    <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" p={{ base: "4", md: "6" }}>
      <Flex
        direction={{ base: "column", md: "row" }}
        justify="space-between"
        align={{ base: "stretch", md: "flex-start" }}
        gap="3"
        mb="4"
      >
        <Breadcrumb
          separator={<Icon as={FiChevronRight} color="smMuted" boxSize="3" />}
          fontSize="sm"
          flex="1"
          minW="0"
        >
          <BreadcrumbItem>
            <BreadcrumbLink as={RouterLink} to={urls.v2.sourcingEvents.viewAll} color="smPrimary">
              {t("All procurements")}
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem isCurrentPage>
            <BreadcrumbLink color="smMuted" isTruncated maxW={{ base: "220px", md: "360px", lg: "520px" }}>
              {eventData.title || t("Untitled")}
            </BreadcrumbLink>
          </BreadcrumbItem>
        </Breadcrumb>

        <HStack spacing="2" flexShrink={0} justify={{ base: "flex-start", md: "flex-end" }} flexWrap="wrap">
          {shareControl}
          <DevFastForwardMenu event={event} />
          <EventLifecycleMenu event={event} />
          {onViewPublic && (
            <Button size="sm" variant="outline" leftIcon={<Icon as={FiExternalLink} />} onClick={onViewPublic}>
              {t("View as supplier")}
            </Button>
          )}
          <Explanation text={t("Complete the evaluation first")} enabled={!canGenerateProtocol}>
            <Button
              size="sm"
              variant="outline"
              leftIcon={<Icon as={FiFileText} />}
              isDisabled={!canGenerateProtocol}
              onClick={() => navigate(urls.v2.sourcingEvents.protocol.url(event.id))}
            >
              {t("Generate protocol")}
            </Button>
          </Explanation>
        </HStack>
      </Flex>

      <Stack spacing="2" mb="5">
        <Heading as="h1" size="lg" color="smText" lineHeight="1.15">
          {eventData.title || t("Untitled")}
        </Heading>
        <HStack spacing="2" align="center" flexWrap="wrap">
          <SourcingEventV2StatusTag status={event.status} />
          {isPrivateEvent && (
            <Tag colorScheme="gray" variant="subtle">
              {terms.simplifiedRegime}
            </Tag>
          )}
          {stage && event.status === SourcingEventStatus.Published && (
            <Tag colorScheme="purple" variant="subtle">
              {t(STAGE_LABEL_KEYS[stage])}
            </Tag>
          )}
        </HStack>
        <RoundPager justify="flex-start" />
      </Stack>

      <LabelValueGrid
        rows={[
          {
            label: t("Responsible buyer"),
            value: (
              <HStack spacing="2" flexWrap="wrap">
                <Text as="span">{responsiblePerson ? personName(responsiblePerson) : DASH}</Text>
                {responsibleEditControl}
              </HStack>
            ),
          },
          ...(isTwoStage && !isQualificationScheme(publicContext?.procedureType)
            ? [
                {
                  label: isDpsScheme(publicContext?.procedureType)
                    ? t("Deadline for the first intake")
                    : t("Deadline for requests to participate"),
                  value: requestDeadline ? displayDateWithTime(requestDeadline) : DASH,
                },
              ]
            : []),
          {
            label: t("Deadline"),
            value: bidDeadline ? displayDateWithTime(bidDeadline) : DASH,
          },
          ...(publicContext
            ? [
                {
                  label: t("Question deadline"),
                  value: event.questionDeadline ? displayDateWithTime(event.questionDeadline) : DASH,
                },
              ]
            : []),
          ...(publicContext
            ? [
                {
                  label: t("Procedure"),
                  value: publicContext.procedureType ? getProcedureLabel(publicContext.procedureType, t) : DASH,
                },
              ]
            : []),
          ...(publicContext
            ? [
                {
                  label: t("Contract type"),
                  value: contractNature ? getContractNatureLabel(contractNature, t) : DASH,
                },
                {
                  label: t("Estimated value"),
                  value: value?.value != null ? formatEstimatedValue(value.value, value.currency) : DASH,
                },
              ]
            : []),
          ...(doffinReference
            ? [
                {
                  label: t("Doffin reference"),
                  value: (
                    <Link
                      href={buildDoffinNoticeUrl(doffinReference)}
                      isExternal
                      rel="noopener noreferrer"
                      color="smPrimary"
                      display="inline-flex"
                      alignItems="center"
                      gap="1"
                    >
                      {doffinReference}
                      <Icon as={FiExternalLink} boxSize="3" />
                    </Link>
                  ),
                },
              ]
            : []),
        ]}
      />
    </Box>
  );
};
